/* eslint-disable no-unused-vars */
import { memo, useCallback, useMemo, useState, useEffect, useId } from 'react';
import { useComputed, useSignalEffect, useSignal } from '@preact/signals-react';
import { useSelector, useDispatch } from 'react-redux';
import { creativesActions, trackingActions } from 'store/actions';
import { select_is_directBuy } from 'store/selectors';

const defaultErrorObj = { errorValue: false, errorText: '' };

const isRequiredField = {
  Deep_Link: 'deeplink',
  Impression_Link: 'impression',
  Tracking_Link: 'tracking',
  VTA_Link: 'vta',
};

const isValidField = {
  Deep_Link: 'deep',
  Impression_Link: 'impression',
  Tracking_Link: 'tracking',
  VTA_Link: 'vta',
};

const exstraIsValidField = {
  Deep_Link: 'tracking',
  Impression_Link: 'vta',
  Tracking_Link: 'deep',
  VTA_Link: 'impression',
};

const validator_domains_field = {
  Tracking_Link: 'tracking_domains',
  VTA_Link: 'vta_domains',
  Deep_Link: 'deeplink_domains',
  Impression_Link: 'impression_domains',
};

const extra_validator_domains_field = {
  Tracking_Link: 'deeplink_domains',
  VTA_Link: 'impression_domains',
  Deep_Link: 'tracking_domains',
  Impression_Link: 'vta_domains',
};

// refactor TO-DO: check why we use extra_validator_domains_field for a wrong 'name' for Impression domain
// refactor TO-DO: chech why tc_number can come '' on the first load
const getAllowedImpressionDomains = (linkValidator, click_tc_number, name) => {
  const field = validator_domains_field[name];
  const extraField = extra_validator_domains_field[name];

  const domain = linkValidator.find((item) => item.tc_number === click_tc_number);

  let allowedDomainsArr = [];
  if (domain && click_tc_number == '4') {
    // use both VTA and Impression domains only for Adjust (for lagacy reasons)
    allowedDomainsArr = [...(domain[field] || []), ...(domain[extraField] || [])];
  } else {
    allowedDomainsArr = (domain[field]?.length ? domain[field] : domain[extraField]) || [];
  }
  return allowedDomainsArr.map((item) => item.tc_domain_name);
};

function isAllowedImpressionDomains(link, linkValidator, click_tc_number, name) {
  if (!click_tc_number || !link || !name || !linkValidator) return false;

  const allowedList = getAllowedImpressionDomains(linkValidator, click_tc_number, name);
  const formatedLink = link.toLowerCase();

  // allow any preffix for the allowed link
  return allowedList.some((allowedLink) => {
    return allowedLink.includes(formatedLink) || formatedLink.includes(allowedLink);
  });
}

const getValidateParams = (currentDomain, linkValidator, name) => {
  if (currentDomain !== null) {
    const currentValidator = linkValidator?.find((item) => {
      let domainsArr =
        item[validator_domains_field[name]] === null
          ? item[extra_validator_domains_field[name]]
          : item[validator_domains_field[name]];
      domainsArr = domainsArr?.map((domain) => domain.tc_domain_name);
      return domainsArr?.some(
        (link) => link.includes(currentDomain) || currentDomain.includes(link),
      );
    });
    const paramsArr =
      currentValidator[validator_domains_field[name]] !== null
        ? currentValidator[`${isValidField[name]}_params_android`]
        : currentValidator[`${exstraIsValidField[name]}_params_android`];

    const paramsArrToCheck = paramsArr
      ?.map((param) => {
        if (param.tcp_is_mandatory) {
          return param.param_values?.map((item) => item.tcp_params);
        }
      })
      ?.filter(Boolean);
    return paramsArrToCheck;
  }
};

const ErrorAppNextText = () => {
  return (
    <p>
      Custom s2s attribution is not allowed, for more information please contact your account
      manager directly, or at:{' '}
      <a href='mailto:support@appnext.com' target='_blank' rel='noreferrer'>
        support@appnext.com
      </a>
    </p>
  );
};

function validateDomain(domain) {
  const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z0-9]{2,11}?$/;
  return domainRegex.test(domain);
}

export const useValidate = (name, tc_number$, click_tc_number, cleanData$, isSecondSection) => {
  const dispatch = useDispatch();
  const bidType = useSelector(
    (state) => state?.general?.mainCampaignObject?.currentCampaignObj?.bidType,
  );
  const isWebsite = useSelector(
    (state) => state?.general?.mainCampaignObject?.currentCampaignObj?.isWebsite,
  );
  const isReEngageGoal = useSelector(
    (state) => state?.general?.mainCampaignObject?.currentCampaignObj?.reEngageGoal,
  );
  const isDirectBuy = useSelector(select_is_directBuy);

  // const canBeCpm = !window.cpm_is_active ? false : isDirectBuy;
  const isCPMOptional = false; // bidType == 1 && canBeCpm;

  const trackingError = useSelector((state) => state?.tracking?.trackingError);
  const linkValidator = useSelector(
    (state) => state?.creatives?.creativeAndTrackingData?.link_validator_data,
  );
  const isMandatoryField = useSelector(
    (state) =>
      state?.creatives?.creativeAndTrackingData?.link_state_model_dic?.[isRequiredField[name]]
        ?.required,
  );
  const error$ = useSignal(defaultErrorObj);

  const isVTANotRequired = useMemo(() => {
    const shouldSkip = click_tc_number === 18 || click_tc_number === 23;
    if (name === 'VTA_Link' && shouldSkip) return true;
    return false;
  }, [click_tc_number, name]);

  const currentIsMandatoryField = useMemo(() => {
    if ((isSecondSection && tc_number$.value === 1) || isVTANotRequired) {
      return false;
    }
    return isMandatoryField;
  }, [isSecondSection, isMandatoryField, tc_number$.value, isVTANotRequired]);

  useEffect(() => {
    if (trackingError[name] !== error$.value.errorValue) {
      dispatch(
        trackingActions.trackingErrorUpdate({
          fieldName: name,
          errorValue: error$.value.errorValue,
        }),
      );
    }
  }, [name, linkValidator, error$.value]);

  const domainsObj = useMemo(() => {
    const res = linkValidator?.flatMap((item) => {
      const validationIsRequire =
        item[validator_domains_field[name]] !== null
          ? item[`${isValidField[name]}_link_require_validation`]
          : item[`${exstraIsValidField[name]}_link_require_validation`];

      const currentDomains =
        item[validator_domains_field[name]] === null
          ? item[extra_validator_domains_field[name]]
          : item[validator_domains_field[name]];
      const res =
        currentDomains !== null
          ? currentDomains?.map((item) => {
              return { ...item, validationIsRequire };
            })
          : null;
      return res;
    });
    return res.filter(Boolean);
  }, [name, linkValidator]);

  const s2s_tracking_enabled = useSelector(
    (state) => state?.creatives?.creativeAndTrackingData?.s2s_tracking_enabled,
  );
  // always allow S2S for Web Apps
  const isS2SEnabled = s2s_tracking_enabled || isWebsite;

  const validationIsRequire = useMemo(() => {
    let res = domainsObj?.map((item) => item.validationIsRequire);
    res = new Set([...res])?.values()?.next().value;
    return res;
  }, [domainsObj]);

  const domainsArr = useMemo(() => {
    const res = domainsObj?.map((item) => item.tc_domain_name);
    return res;
  }, [domainsObj]);

  const saveTc_number = (link) => {
    try {
      const currentDomain = new URL(link);
      const currentValidator = linkValidator?.find((item) => {
        let domainsArr =
          item[validator_domains_field[name]] === null
            ? item[extra_validator_domains_field[name]]
            : item[validator_domains_field[name]];
        domainsArr = domainsArr?.map((domain) => domain.tc_domain_name);
        return domainsArr?.some((domain) => currentDomain.host.includes(domain));
      });

      if (currentValidator) {
        tc_number$.value = currentValidator.tc_number;
        return true;
      } else {
        if (isS2SEnabled) {
          tc_number$.value = 1;
        } else {
          tc_number$.value = '';
        }
        return false;
      }
    } catch (err) {
      //console.log(err, link);
      return false;
    }
  };

  const handleValidation = (link, matchingDomains, click_tc_number, isSecondSection) => {
    // if (!link && !isMandatoryField) {
    //   return (error$.value = defaultErrorObj);
    // }
    // if (!link && isMandatoryField) {
    //   return (error$.value = {
    //     errorValue: true,
    //     errorText: `Please add valid ${isRequiredField[name]} link`,
    //   });
    // }
    if (!link && isCPMOptional) return true;
    if (!link) {
      error$.value = currentIsMandatoryField
        ? {
            errorValue: true,
            errorText: `Please add valid ${isRequiredField[name]} link`,
          }
        : defaultErrorObj;
      return false;
    }
    try {
      const url = new URL(link.toLowerCase());
      if (url.protocol !== 'https:') {
        error$.value = {
          errorValue: true,
          errorText: 'Make sure that the tracking link starts with https://',
        };
        return false;
      } else {
        error$.value = defaultErrorObj;
      }

      if (!validateDomain(url.hostname)) {
        error$.value = {
          errorValue: true,
          errorText: 'url is not valid',
        };
        return;
      } else {
        error$.value = defaultErrorObj;
      }

      if (
        !isWebsite &&
        !isReEngageGoal &&
        isSecondSection &&
        !isAllowedImpressionDomains(url.hostname, linkValidator, click_tc_number, name) &&
        !isS2SEnabled
        // TO-DO include one more condition validationIsRequire ?
      ) {
        error$.value = {
          errorValue: true,
          errorText: <ErrorAppNextText />,
        };
        return;
      }

      if (validationIsRequire) {
        if (!saveTc_number(link) && !isS2SEnabled) {
          error$.value = {
            errorValue: true,
            errorText: <ErrorAppNextText />,
          };
          return;
        } else {
          error$.value = defaultErrorObj;
        }
        const mainDomains = matchingDomains ? matchingDomains : domainsArr;

        const isInMainDomains = mainDomains?.some(
          (link) => url.host.includes(link) || link.includes(url.host),
        );
        if (isInMainDomains) {
          const paramsArrToCheck = getValidateParams(url.host, linkValidator, name);

          if (paramsArrToCheck) {
            const splitedParams = paramsArrToCheck?.map((param) => {
              return param?.map((str) => str.split('='));
            });

            for (const params of splitedParams) {
              for (const [key, value] of params) {
                if (key) {
                  const containValue = url.searchParams.get(decodeURIComponent(key));

                  if (!containValue) {
                    error$.value = {
                      errorValue: true,
                      errorText: `Missing mandatory parameter(s): ${key}=${value}`,
                    };
                    return false;
                  } else {
                    const shouldBe = splitedParams
                      ?.flatMap((param) =>
                        param?.filter((item) => item[0] === key)?.map((item) => item[1]),
                      )
                      ?.filter((item) => item.length);

                    if (shouldBe?.includes(containValue)) {
                      error$.value = defaultErrorObj;
                    } else {
                      error$.value = {
                        errorValue: true,
                        errorText: `Missing mandatory parameter(s): ${key}=${value}`,
                      };
                      return false;
                    }
                  }
                } else {
                  let shouldBe2 = splitedParams
                    ?.map((param) => param?.filter((item) => !item[0])?.map((item) => item[1]))
                    ?.filter((item) => item.length);
                  shouldBe2 = shouldBe2.reduce((acc, item) => {
                    acc = { ...acc, [item[0]]: [...item] };
                    return acc;
                  }, {});
                  let subStrNotContained2 = '';
                  for (const strToOutput in shouldBe2) {
                    const macrosArray = shouldBe2[strToOutput];
                    const res = macrosArray.every((macros) => !url.href.includes(macros));
                    if (res) subStrNotContained2 = strToOutput;
                  }

                  if (subStrNotContained2) {
                    error$.value = {
                      errorValue: true,
                      errorText: `url should contain ${subStrNotContained2}`,
                    };
                    return false;
                  } else {
                    error$.value = defaultErrorObj;
                  }
                }
              }
            }
          }
        } else {
          //error$.value = { errorValue: true, errorText: 'url is not valid1' };
        }
      }

      return true;
    } catch (e) {
      //console.log(e);
      error$.value = { errorValue: true, errorText: 'url is not valid' };
      return false;
    }
  };

  return {
    handleValidation,
    error$,
    saveTc_number,
    isMandatoryField,
    currentIsMandatoryField,
  };
};
